import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Tab,
  Tabs,
} from "@mui/material";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  useGetManagedGroupQuery,
  useDeleteGroupMutation,
} from "../../api/managementApi";
import { gradientFor } from "../../lib/groupColorPalette";
import LoadingView from "../../components/ui/LoadingView";
import { useToast } from "../../components/ui/Toast";
import InlineGroupNameEdit from "./components/InlineGroupNameEdit";
import PersonList from "./components/PersonList";
import AddPersonModal from "./components/AddPersonModal";
import QrTemplateList from "./components/QrTemplateList";
import DeleteGroupConfirm from "./components/DeleteGroupConfirm";
import EditUserGoalsModal from "./components/EditUserGoalsModal";
import GroupHighlightsView from "../groups/components/insights/GroupHighlightsView";
import GroupTitlesView from "../groups/components/insights/GroupTitlesView";
import ConnectionState from "../../components/ui/ConnectionState";

const TR_MONTHS = [
  "Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
  "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık",
];

function formatTrDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export default function ManagedGroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useScriptMode();
  const toast = useToast();
  const { user } = useAuth();
  const currentUserId = user?._id;

  const { data, isLoading, isFetching, isError, error, refetch } = useGetManagedGroupQuery({ groupId });
  const group = data?.data?.group;

  const [addPersonMode, setAddPersonMode] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editGoalsMember, setEditGoalsMember] = useState(null);
  const [insightTab, setInsightTab] = useState(0);
  const [deleteGroup, { isLoading: deleteLoading }] = useDeleteGroupMutation();

  if (isLoading) return <LoadingView label={t("loading")} />;

  if (isError || !group) {
    return (
      <div className="max-w-3xl mx-auto py-16 flex flex-col items-center gap-4">
        {isError ? (
          <ConnectionState
            error={error}
            onRetry={refetch}
            retrying={isFetching}
            message={t("manage_detail_load_error")}
          />
        ) : (
          <Typography variant="h6">{t("manage_detail_load_error")}</Typography>
        )}
        <Button component={Link} to="/uygulama/yonetim" variant="outlined">
          {t("manage_detail_back")}
        </Button>
      </div>
    );
  }

  const banner = gradientFor(group.groupName);

  const handleDeleteGroup = async () => {
    try {
      await deleteGroup({ groupId: group.id }).unwrap();
      toast.success(t("manage_delete_success"));
      setDeleteOpen(false);
      setTimeout(() => navigate("/uygulama/yonetim"), 250);
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("manage_delete_error");
      toast.error(msg);
    }
  };

  const handlePersonAdded = (mode) => {
    toast.success(
      mode === "manager" ? t("manage_manager_added") : t("manage_member_added")
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="max-w-3xl mx-auto flex flex-col gap-5 px-4 md:px-0"
    >
      <Link to="/uygulama/yonetim" className="text-terakkiPrimary text-sm">
        {t("manage_detail_back")}
      </Link>

      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        <Box sx={{ height: 80, background: banner }} />
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#2E7D62" }}>
              {group.groupName}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                size="small"
                component={Link}
                to={`/uygulama/yonetim/${group.id}/istatistikler`}
              >
                {t("manage_stats_cta")}
              </Button>
              <InlineGroupNameEdit groupId={group.id} currentName={group.groupName} />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {group.memberCount} {t("groups_member_count")} ·{" "}
            {group.totalManagers} {t("groups_manager_count")} ·{" "}
            {formatTrDate(group.createdAt)}
          </Typography>
          {group.description ? (
            <Typography variant="body2" mt={1.5}>
              {group.description}
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      {/* Enler ve Ünvanlar: grup sayfasındaki sekmelerin aynısı. */}
      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        <div className="border-b border-terakkiBorder dark:border-white/15 px-2">
          <Tabs value={insightTab} onChange={(_, v) => setInsightTab(v)} variant="fullWidth">
            <Tab label={t("Enler")} sx={{ textTransform: "none", fontSize: 15, fontWeight: 500 }} />
            <Tab label={t("Ünvanlar")} sx={{ textTransform: "none", fontSize: 15, fontWeight: 500 }} />
          </Tabs>
        </div>
        <div className="h-[520px] flex flex-col">
          {insightTab === 0 ? (
            <GroupHighlightsView groupId={String(group.id)} />
          ) : (
            <GroupTitlesView groupId={String(group.id)} />
          )}
        </div>
      </Card>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("manage_section_members")}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setAddPersonMode("member")}>
              {t("manage_add_member_cta")}
            </Button>
          </Box>
          <PersonList
            groupId={group.id}
            role="member"
            people={group.members}
            currentUserId={currentUserId}
            emptyText={t("manage_members_empty")}
            onEditGoals={(person) => setEditGoalsMember(person)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("manage_section_managers")}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setAddPersonMode("manager")}>
              {t("manage_add_manager_cta")}
            </Button>
          </Box>
          <PersonList
            groupId={group.id}
            role="manager"
            people={group.managers}
            currentUserId={currentUserId}
            emptyText={t("manage_managers_empty")}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} mb={1}>
            {t("manage_section_templates")}
          </Typography>
          <QrTemplateList
            groupId={group.id}
            groupName={group.groupName}
            templates={group.qrTemplates}
          />
        </CardContent>
      </Card>

      <Divider />

      <Card sx={{ borderColor: "error.main", borderWidth: 1, borderStyle: "solid" }}>
        <CardContent>
          <Typography variant="subtitle1" color="error" sx={{ fontWeight: 600 }}>
            {t("manage_danger_zone")}
          </Typography>
          <Box mt={2} display="flex" justifyContent="flex-end">
            {/* Silme dolu kırmızı: iki temada da belli olsun. */}
            <Button
              variant="contained"
              disableElevation
              onClick={() => setDeleteOpen(true)}
              sx={{
                borderRadius: 999,
                backgroundColor: "#C0392B",
                color: "#fff",
                "&:hover": { backgroundColor: "#A93226" },
              }}
            >
              {t("manage_delete_group_cta")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <AddPersonModal
        open={!!addPersonMode}
        mode={addPersonMode || "member"}
        groupId={group.id}
        onClose={() => setAddPersonMode(null)}
        onAdded={handlePersonAdded}
      />
      <DeleteGroupConfirm
        open={deleteOpen}
        groupName={group.groupName}
        loading={deleteLoading}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteGroup}
      />
      <EditUserGoalsModal
        open={!!editGoalsMember}
        groupId={group.id}
        // Güncel üye: hedef değişince grup yeniden çekilir, liste tazelenir.
        member={(group.members || []).find((m) => String(m.id) === String(editGoalsMember?.id)) || editGoalsMember}
        onClose={() => setEditGoalsMember(null)}
      />
    </motion.div>
  );
}
